//  Purpose: Firebase Auth Store
import axios from 'axios';
import { create } from 'zustand';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
// import { doc, getDoc, setDoc, collection, getFirestore } from 'firebase/firestore';
import {
  // User,
  // getAuth,
  getAuth,
  signOut,
  initializeAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  sendPasswordResetEmail,
  indexedDBLocalPersistence,
  // signInWithEmailAndPassword,
  // createUserWithEmailAndPassword,
} from 'firebase/auth';

import { saveAccountId, clearLocalDataExceptAccountId } from 'src/utils/Services/DB_Utils';
import { SECURE_STORAGE_KEYS, secureStorageService } from 'src/utils/Services/secure-storage.service';

import { AuthUserType } from 'src/auth/types';
import useUserDataStore from 'src/store/user';
import { useSystemStore } from 'src/store/SystemStore';

import { getFirebaseWebApp } from './lib';

// Conditional logger - only logs in development mode
const isDev = import.meta.env.DEV;
const authLogger = {
  log: (...args: unknown[]): void => {
    if (isDev) console.log('[AuthStore]', ...args);
  },
  warn: (...args: unknown[]): void => {
    if (isDev) console.warn('[AuthStore]', ...args);
  },
  error: (...args: unknown[]): void => {
    if (isDev) console.error('[AuthStore]', ...args);
  },
};

// Unique ID to detect module duplication issues
export const AUTH_MODULE_ID = Math.random().toString(36).substring(7);
authLogger.log('MODULE LOADED, ID:', AUTH_MODULE_ID);

/**
 * Wrap a promise with a timeout to prevent hanging on iOS.
 * Web SDK operations can hang indefinitely due to CORS/GAPI issues in Capacitor WebView.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, errorMessage: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(errorMessage)), ms);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

// Timeout for web SDK sync operations (5 seconds)
const WEB_SDK_SYNC_TIMEOUT = 5000;

// Promise that resolves when Firebase auth state is first determined
let authReadyPromise: Promise<void> | null = null;
let authReadyResolve: (() => void) | null = null;

// Track token refresh interval to prevent multiple intervals
let tokenRefreshIntervalId: NodeJS.Timeout | null = null;

// Track if auth ready has been resolved to prevent double-resolution
let authReadyResolved = false;

// Track if web SDK sync has failed - used to trigger re-authentication
let webSdkSyncFailed = false;

// CRITICAL: Track if we've successfully authenticated to prevent race condition resets
// Once this is set to true, auth state should NOT be reset unless explicitly logged out
let authenticationCompleted = false;

/**
 * Check if web SDK sync has failed and re-auth may be needed
 */
export function needsReAuthentication(): boolean {
  return webSdkSyncFailed;
}

/**
 * Returns true if the last web SDK auth sync failed.
 * When true, Firestore/RealtimeDB security rules may reject requests
 * because the web SDK has no authenticated user with valid claims.
 */
export function isWebSdkSyncFailed(): boolean {
  return webSdkSyncFailed;
}

/**
 * Attempt to sync web SDK auth using a fresh custom token from the backend.
 * This is called when the stored custom token has expired.
 * Returns true if sync was successful, false otherwise.
 */
export async function attemptWebSdkSyncWithFreshToken(): Promise<boolean> {
  authLogger.log('Attempting to get fresh custom token for web SDK sync...');

  try {
    // Get the native ID token to authenticate with backend
    const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
    if (!idTokenResult.token) {
      authLogger.error('Failed to get native ID token for refresh');
      return false;
    }

    // Get device info for the request
    const [deviceInfo, deviceId] = await Promise.all([Device.getInfo(), Device.getId()]);

    // Request a fresh custom token from the backend
    const { baseUrl } = useSystemStore.getState();
    const response = await fetch(`${baseUrl}/api/auth/refreshCustomToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idTokenResult.token}`,
      },
      body: JSON.stringify({
        device: {
          id: deviceId.identifier,
          platform: deviceInfo.platform,
          model: deviceInfo.model,
          osVersion: deviceInfo.osVersion,
          manufacturer: deviceInfo.manufacturer,
          isVirtual: deviceInfo.isVirtual,
          webViewVersion: deviceInfo.webViewVersion,
        },
      }),
    });

    if (!response.ok) {
      authLogger.warn('Backend refresh token endpoint returned:', response.status);
      // This endpoint might not exist yet - that's okay, we'll fall back gracefully
      return false;
    }

    const data = await response.json();
    const freshToken = data?.token || data?.result?.token;

    if (!freshToken) {
      authLogger.warn('No token in refresh response');
      return false;
    }

    // Try to sign in with the fresh token (with timeout to prevent hanging on iOS)
    const auth = getFirebaseAuth();
    if (auth) {
      await withTimeout(
        signInWithCustomToken(auth, freshToken),
        WEB_SDK_SYNC_TIMEOUT,
        'Fresh custom token sign-in timed out'
      );
      // Save the fresh token for future use
      secureStorageService.set(SECURE_STORAGE_KEYS.CUSTOM_TOKEN, freshToken);
      authLogger.log('Successfully synced web SDK with fresh custom token');
      webSdkSyncFailed = false;
      return true;
    }
  } catch (error) {
    authLogger.warn('Failed to sync web SDK with fresh token (may have timed out):', error);
  }

  return false;
}

/**
 * Returns a promise that resolves when Firebase auth state has been determined.
 * This ensures guards wait for Firebase to restore the session before making auth decisions.
 * Includes a 10 second timeout fallback to prevent the app from hanging indefinitely
 * if onAuthStateChanged doesn't fire (can happen on some Android devices).
 */
export function waitForAuthReady(): Promise<void> {
  authLogger.log('waitForAuthReady called from MODULE:', AUTH_MODULE_ID);
  authLogger.log('waitForAuthReady state:', {
    authReadyResolved,
    hasPromise: !!authReadyPromise,
  });

  if (!authReadyPromise) {
    authLogger.log('Creating new authReadyPromise with 10s timeout');
    authReadyPromise = new Promise<void>((resolve) => {
      authReadyResolve = resolve;

      // Safety timeout: if auth isn't ready in 10 seconds, resolve anyway
      // This prevents the app from hanging forever if onAuthStateChanged doesn't fire
      setTimeout(() => {
        if (!authReadyResolved) {
          authLogger.warn('Auth ready timeout (10s) - resolving anyway to prevent hang');
          authLogger.warn('Debug state at timeout:', {
            authenticated: useAuthStore.getState().authenticated,
            authInitialized: useAuthStore.getState().authInitialized,
            loading: useAuthStore.getState().loading,
            hasUser: !!useAuthStore.getState().user,
          });
          authReadyResolved = true;
          useAuthStore.setState({ authInitialized: true, loading: false });
          resolve();
        }
      }, 10000);
    });
  } else {
    authLogger.log('authReadyPromise already exists, reusing from MODULE:', AUTH_MODULE_ID);
  }
  return authReadyPromise;
}

// Cache the auth instance to avoid re-initialization issues
let cachedAuth: ReturnType<typeof initializeAuth> | ReturnType<typeof getAuth> | null = null;

// const DB = getFirestore(firebaseApp);
/**
 * Get Firebase Auth instance for web SDK operations.
 * This is available on all platforms for:
 * - Non-auth operations like getting user claims
 * - Auth operations on web platform
 *
 * Note: For actual auth operations (login, logout, auth state) on native platforms,
 * use FirebaseAuthentication plugin directly in the relevant methods.
 */
export const getFirebaseAuth = () => {
  // Return cached instance if available
  if (cachedAuth) {
    return cachedAuth;
  }

  const firebaseApp = getFirebaseWebApp();

  if (Capacitor.isNativePlatform()) {
    // On native platforms, use initializeAuth with indexedDBLocalPersistence
    // This avoids the GAPI iframe initialization that causes CORS errors on iOS
    // See: https://github.com/firebase/firebase-js-sdk/issues/5019
    authLogger.log('Using initializeAuth with indexedDBLocalPersistence for native platform');
    cachedAuth = initializeAuth(firebaseApp, {
      persistence: indexedDBLocalPersistence,
    });
  } else {
    // On web, use getAuth for full browser support
    cachedAuth = getAuth(firebaseApp);
  }

  return cachedAuth;
};

// const FIREBASE_AUTH = getAuth(firebaseAppOnlyForWeb);
// const AUTH = getFirebaseAuth();
interface AuthState {
  user: AuthUserType | null;
  authToken: string | null;
  authenticated: boolean;
  loading: boolean;
  authInitialized: boolean;

  login: (accountNumber: string, code: string, isAdminOverride?: boolean) => Promise<void>;
  // Auth V2: sign in with a Firebase custom token from the business-device pairing flow.
  loginWithCustomToken: (customToken: string) => Promise<void>;
  // loginWithGoogle: () => Promise<void>;
  // loginWithGithub: () => Promise<void>;
  // loginWithTwitter: () => Promise<void>;
  // register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  getToken: () => Promise<string | null>;
  initializeAuth: () => Promise<void>;
}

const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  authToken: null,
  authenticated: false,
  loading: true,
  authInitialized: false, // New state to track auth initialization

  initializeAuth: async () => {
    // Simply wait for the auth listener to complete its first callback
    // This ensures Firebase has restored the session before we proceed
    await waitForAuthReady();
  },
  login: async (accountNumber, code, isAdminOverride = false) => {
    try {
      // Use different endpoint for admin override
      const endpoint = isAdminOverride
        ? `${useSystemStore.getState().baseAthUrl}/loginUser?type=adminOverride`
        : `${useSystemStore.getState().baseAthUrl}/loginUser?type=verifyCode`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accountNumber,
          verificationCode: code,
          isAdminOverride,
        }),
      });
      authLogger.log('login response', response);

      const responseJson = await response.json();
      authLogger.log('login responseJson', responseJson);
      if (responseJson == null) {
        throw new Error('Invalid verification code');
      }
      const token = responseJson?.token || responseJson?.result?.token;

      await saveAccountId(accountNumber);

      // Store the custom token securely for potential session restoration
      // Note: Custom tokens expire after 1 hour, but this helps with quick app restarts
      if (Capacitor.isNativePlatform()) {
        await secureStorageService.set(SECURE_STORAGE_KEYS.CUSTOM_TOKEN, token);
      }

      if (Capacitor.isNativePlatform()) {
        // On native platforms, sign in on BOTH native plugin AND web SDK
        // Native plugin handles the native Firebase SDK auth
        // Web SDK auth is needed for Firestore/other web SDK services to work
        authLogger.log('Using native Firebase plugin for sign in');
        const result = await FirebaseAuthentication.signInWithCustomToken({ token });
        const { user } = result;

        if (!user) {
          throw new Error('[AuthStore] User not found after native sign in');
        }

        // CRITICAL: Also sign in on web SDK so Firestore works
        // Firestore uses web SDK which needs to see the authenticated user
        const auth = getFirebaseAuth();
        if (auth) {
          authLogger.log('Also signing in on web SDK for Firestore compatibility');
          await signInWithCustomToken(auth, token);
          // Check claims WITHOUT force-refresh. The first ID token issued by
          // signInWithCustomToken embeds claims from the custom token payload.
          // Force-refreshing immediately LOSES those embedded claims because Firebase
          // re-issues via the refresh-token path which only includes setCustomUserClaims
          // claims (which the backend must call separately for persistence).
          const webUser = auth.currentUser;
          if (webUser) {
            const claimsResult = await webUser.getIdTokenResult(false).catch(() => null);
            console.log('[AuthStore] Web SDK claims after login sync (no force-refresh):', {
              businessId: claimsResult?.claims.businessId ?? null,
              role: claimsResult?.claims.role ?? null,
              issuedAtTime: claimsResult?.issuedAtTime,
            });
            if (!claimsResult?.claims.businessId) {
              console.warn('[AuthStore] businessId claim missing after login sync. Backend must call setCustomUserClaims(uid, { businessId }) to persist claims across token refreshes.');
            }
          }
        }

        // Get ID token from native layer
        const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
        const authToken = idTokenResult.token;

        // Set authenticated state immediately
        authLogger.log('LOGIN SUCCESS - Setting auth state:', {
          uid: user.uid,
          authenticated: true,
          authInitialized: true,
        });

        // CRITICAL: Mark authentication as completed BEFORE setState
        authenticationCompleted = true;

        // Track in window for debugging
        (window as any).__AUTH_DEBUG__?.push({
          event: 'loginSuccess',
          uid: user.uid,
          authenticationCompleted: true,
          time: Date.now(),
        });

        useAuthStore.setState({
          user: {
            uid: user.uid,
            email: user.email || null,
            displayName: user.displayName || null,
            photoURL: user.photoUrl || null,
            emailVerified: user.emailVerified || false,
            id: user.uid,
            role: 'admin',
          },
          authToken,
          authenticated: true,
          loading: false,
          authInitialized: true,
        });

        // Verify state was set correctly
        const stateAfterLogin = useAuthStore.getState();
        authLogger.log('🔑 State AFTER login setState:', {
          authenticated: stateAfterLogin.authenticated,
          authInitialized: stateAfterLogin.authInitialized,
          hasUser: !!stateAfterLogin.user,
        });

        // Save the auth token to local storage
        localStorage.setItem('authToken', authToken);

        // CRITICAL FIX: Resolve auth ready promise after successful login
        // This prevents the 10-second timeout from firing since we know auth succeeded
        if (authReadyResolve && !authReadyResolved) {
          authLogger.log('🔑 Auth ready - resolving after successful native login');
          authReadyResolved = true;
          authReadyResolve();
        }
      } else {
        // Use web SDK for authentication
        const auth = getFirebaseAuth();
        if (!auth) {
          throw new Error('[AuthStore] Firebase auth not initialized');
        }

        const userCredential = await signInWithCustomToken(auth, token);
        const { user } = userCredential;

        if (!user) {
          throw new Error('[AuthStore] User not found in login by account token');
        }

        // IMPORTANT: Update authentication state immediately after successful login
        // This ensures the redirect happens without waiting for onAuthStateChanged
        const authToken = await user.getIdToken(true);
        const profile = {
          displayName: user.displayName,
          email: user.email,
          photoURL: user.photoURL,
        };

        // Set authenticated state immediately
        useAuthStore.setState({
          user: {
            ...user,
            ...profile,
            id: user.uid,
            role: 'admin',
          },
          authToken,
          authenticated: true,
          loading: false,
          authInitialized: true,
        });

        // Save the auth token to local storage
        localStorage.setItem('authToken', authToken);

        // CRITICAL FIX: Resolve auth ready promise after successful login
        // This prevents the 10-second timeout from firing since we know auth succeeded
        if (authReadyResolve && !authReadyResolved) {
          authLogger.log('Auth ready - resolving after successful web login');
          authReadyResolved = true;
          authReadyResolve();
        }
      }
    } catch (error) {
      authLogger.error('Login error:', error);
      if (error instanceof Error && error.message.includes('Invalid verification code')) {
        throw error;
      }
      throw new Error('Unable to login, by account verification');
    }
  },
  /**
   * Auth V2: sign in directly with a Firebase custom token returned by the
   * business-device pairing /complete (or /session/refresh) endpoint.
   *
   * Mirrors the custom-token portion of login() — native dual sign-in (native plugin
   * + web SDK) on native, web SDK on web — but is fully additive: it does NOT touch
   * the account-number/verification-code flow. State is set directly (on web too) so
   * the emailVerified gate in the web onAuthStateChanged listener never blocks a
   * device session.
   */
  loginWithCustomToken: async (customToken: string) => {
    try {
      if (Capacitor.isNativePlatform()) {
        // Persist for session restoration and web SDK sync (as login() does).
        await secureStorageService.set(SECURE_STORAGE_KEYS.CUSTOM_TOKEN, customToken);

        const result = await FirebaseAuthentication.signInWithCustomToken({ token: customToken });
        const { user } = result;
        if (!user) {
          throw new Error('[AuthStore] User not found after device custom-token sign in');
        }

        // Also sign in on the web SDK for Firestore/RealtimeDB compatibility (non-fatal).
        const auth = getFirebaseAuth();
        if (auth) {
          await signInWithCustomToken(auth, customToken).catch((err) => {
            authLogger.warn('Web SDK device sign-in failed (non-fatal):', err);
          });
        }

        const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
        const authToken = idTokenResult.token;

        authenticationCompleted = true;
        useAuthStore.setState({
          user: {
            uid: user.uid,
            email: user.email || null,
            displayName: user.displayName || null,
            photoURL: user.photoUrl || null,
            emailVerified: user.emailVerified || false,
            id: user.uid,
            role: 'admin',
          },
          authToken,
          authenticated: true,
          loading: false,
          authInitialized: true,
        });
        localStorage.setItem('authToken', authToken);

        if (authReadyResolve && !authReadyResolved) {
          authReadyResolved = true;
          authReadyResolve();
        }
      } else {
        const auth = getFirebaseAuth();
        if (!auth) {
          throw new Error('[AuthStore] Firebase auth not initialized');
        }

        const userCredential = await signInWithCustomToken(auth, customToken);
        const { user } = userCredential;
        if (!user) {
          throw new Error('[AuthStore] User not found after device custom-token sign in');
        }

        const authToken = await user.getIdToken(true);

        authenticationCompleted = true;
        useAuthStore.setState({
          user: {
            ...user,
            id: user.uid,
            role: 'admin',
          },
          authToken,
          authenticated: true,
          loading: false,
          authInitialized: true,
        });
        localStorage.setItem('authToken', authToken);

        if (authReadyResolve && !authReadyResolved) {
          authReadyResolved = true;
          authReadyResolve();
        }
      }
    } catch (error) {
      authLogger.error('loginWithCustomToken error:', error);
      throw error instanceof Error ? error : new Error('Unable to sign in with device token');
    }
  },
  // loginWithGoogle: async () => {
  //   const provider = new GoogleAuthProvider();
  //   await signInWithPopup(AUTH, provider);
  // },

  logout: async () => {
    authLogger.log('LOGOUT called - resetting authenticationCompleted flag');

    // CRITICAL: Reset the authentication completed flag on logout
    authenticationCompleted = false;

    // 1. Sign out from both native and web SDK
    if (Capacitor.isNativePlatform()) {
      // Sign out using native Firebase plugin
      await FirebaseAuthentication.signOut();
      authLogger.log('Signed out via native plugin');

      // Also sign out from web SDK (for Firestore compatibility)
      const auth = getFirebaseAuth();
      if (auth) {
        await signOut(auth);
        authLogger.log('Also signed out from web SDK');
      }
    } else {
      // Sign out using web SDK
      const auth = getFirebaseAuth();
      if (auth) {
        await signOut(auth);
        authLogger.log('Signed out via web SDK');
      }
    }

    // 2. Clear local database except accountId
    await clearLocalDataExceptAccountId();

    // 3. Clear auth-related localStorage items
    localStorage.removeItem('authToken');
    await secureStorageService.remove(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);

    // 4. Clear user data store (this uses persist middleware)
    useUserDataStore.getState().signUserOut();

    // 5. Clear token refresh interval to prevent memory leaks
    if (tokenRefreshIntervalId) {
      clearInterval(tokenRefreshIntervalId);
      tokenRefreshIntervalId = null;
    }

    set({ user: null, authToken: null, authenticated: false });
  },
  forgotPassword: async (email) => {
    if (Capacitor.isNativePlatform()) {
      // Use native Firebase plugin
      await FirebaseAuthentication.sendPasswordResetEmail({ email });
    } else {
      const auth = getFirebaseAuth();
      if (auth) {
        await sendPasswordResetEmail(auth, email);
      }
    }
  },
  getToken: async () => {
    if (Capacitor.isNativePlatform()) {
      // Use native Firebase plugin
      try {
        const result = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
        if (!result.token) {
          authLogger.error('getToken: Native token result is empty');
          throw new Error('No auth token found');
        }
        return result.token;
      } catch (error) {
        authLogger.error('getToken: Failed to get native token:', error);
        throw new Error('Failed to get auth token');
      }
    }

    // Use web SDK
    const auth = getFirebaseAuth();
    if (!auth) {
      authLogger.error('getToken: Firebase auth not initialized');
      throw new Error('Firebase auth not initialized');
    }

    const user = auth.currentUser;
    if (!user) {
      authLogger.error('getToken: No current user found');
      throw new Error('No user found');
    }

    try {
      const authToken = await user.getIdTokenResult(true);
      if (!authToken?.token) {
        authLogger.error('getToken: Token result is empty');
        throw new Error('No auth token found');
      }
      return authToken.token;
    } catch (error) {
      authLogger.error('getToken: Failed to get token:', error);
      throw new Error('Failed to get auth token');
    }
  },
}));

// Function to refresh the auth token
const refreshAuthToken = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Use native Firebase plugin
      const result = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
      if (result.token) {
        useAuthStore.setState({ authToken: result.token });
        localStorage.setItem('authToken', result.token);
        authLogger.log('Token refreshed successfully (native)');
      }
    } else {
      // Use web SDK
      const auth = getFirebaseAuth();
      if (!auth) {
        authLogger.log('No auth instance in refreshAuthToken');
        return;
      }
      const user = auth.currentUser;
      if (!user) {
        authLogger.log('No user found in refreshAuthToken');
        return;
      }
      const token = await user.getIdToken(true);
      useAuthStore.setState({ authToken: token });
      // Persist to localStorage so token survives crashes/refreshes
      localStorage.setItem('authToken', token);
      authLogger.log('Token refreshed successfully (web)');
    }
  } catch (error) {
    authLogger.error('Error refreshing token:', error);
  }
};

// Track whether auth listener has been initialized
let authListenerInitialized = false;

/**
 * Initialize the auth state listener
 * This should be called after Firebase is fully initialized on native platforms
 * to prevent race conditions and Firebase configuration errors
 */
export function initializeAuthListener() {
  authLogger.log('initializeAuthListener() CALLED');

  // Initialize secure storage and migrate legacy tokens
  secureStorageService.initialize().then(() => {
    secureStorageService.migrateFromLocalStorage(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);
  }).catch((error) => {
    authLogger.warn('Secure storage initialization failed:', error);
  });

  // Also store in window for debugging
  (window as any).__AUTH_DEBUG__ = (window as any).__AUTH_DEBUG__ || [];
  (window as any).__AUTH_DEBUG__.push({ event: 'initializeAuthListener', time: Date.now() });

  if (authListenerInitialized) {
    authLogger.log('Auth listener already initialized - skipping');
    return;
  }

  authLogger.log('First time initialization - proceeding...');
  authListenerInitialized = true;

  // Create the ready promise if it doesn't exist
  if (!authReadyPromise) {
    authLogger.log('Creating authReadyPromise in initializeAuthListener, MODULE:', AUTH_MODULE_ID);
    authReadyPromise = new Promise<void>((resolve) => {
      authReadyResolve = resolve;

      // Safety timeout: if auth isn't ready in 10 seconds, resolve anyway
      setTimeout(() => {
        if (!authReadyResolved) {
          authLogger.warn('Auth ready timeout (10s) in initializeAuthListener - resolving anyway');
          authLogger.warn('Debug state at timeout:', {
            authenticated: useAuthStore.getState().authenticated,
            authInitialized: useAuthStore.getState().authInitialized,
            loading: useAuthStore.getState().loading,
            hasUser: !!useAuthStore.getState().user,
            moduleId: AUTH_MODULE_ID,
          });
          authReadyResolved = true;
          useAuthStore.setState({ authInitialized: true, loading: false });
          resolve();
        }
      }, 10000);
    });
  } else {
    authLogger.log('authReadyPromise already exists in initializeAuthListener, MODULE:', AUTH_MODULE_ID);
  }

  let isFirstCallback = true;

  if (Capacitor.isNativePlatform()) {
    // Use native Firebase plugin listener
    authLogger.log('Setting up native authStateChange listener...');

    FirebaseAuthentication.addListener('authStateChange', async (change) => {
      authLogger.log('authStateChange FIRED, user:', change.user?.uid || 'null');
      authLogger.log('State before:', {
        authenticated: useAuthStore.getState().authenticated,
        authInitialized: useAuthStore.getState().authInitialized,
      });

      // Track in window for debugging
      (window as any).__AUTH_DEBUG__?.push({
        event: 'authStateChange',
        user: change.user?.uid || null,
        time: Date.now(),
      });
      try {
        if (change.user) {
          // For custom token auth, email verification is handled differently
          // Custom tokens typically don't require email verification check
          const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });

          // CRITICAL: Sync web SDK auth for Firestore compatibility
          // Check if web SDK has the user, if not try to sign in
          let webSdkSynced = false;
          const auth = getFirebaseAuth();

          if (auth?.currentUser) {
            // Web SDK already has user (session restored from IndexedDB).
            // Check if their token still has businessId. After ~1 hour Firefox auto-refreshes
            // the token via the refresh-token path, which drops embedded custom-token claims
            // unless the backend also calls setCustomUserClaims.
            const existingClaims = await auth.currentUser.getIdTokenResult(false).catch(() => null);
            const hasBusinessId = Boolean(existingClaims?.claims.businessId);
            console.log('[AuthStore] authStateChange — web SDK session restored:', {
              uid: auth.currentUser.uid,
              hasBusinessId,
              issuedAtTime: existingClaims?.issuedAtTime,
            });
            if (hasBusinessId) {
              webSdkSynced = true;
              webSdkSyncFailed = false;
            } else {
              // Claims were lost to token refresh. Try to re-sync with a fresh custom token
              // so the next ID token has the embedded businessId claim again.
              console.warn('[AuthStore] businessId claim missing from restored web SDK session — attempting re-sync...');
              try {
                const freshSyncSuccess = await withTimeout(
                  attemptWebSdkSyncWithFreshToken(),
                  WEB_SDK_SYNC_TIMEOUT,
                  'Fresh token re-sync timed out'
                );
                if (freshSyncSuccess) {
                  const resynced = auth.currentUser;
                  const resyncedClaims = resynced
                    ? await resynced.getIdTokenResult(false).catch(() => null)
                    : null;
                  console.log('[AuthStore] After re-sync, businessId claim:', resyncedClaims?.claims.businessId ?? 'STILL MISSING — backend must call setCustomUserClaims');
                  webSdkSynced = Boolean(resyncedClaims?.claims.businessId);
                  webSdkSyncFailed = !webSdkSynced;
                } else {
                  webSdkSyncFailed = true;
                  console.warn('[AuthStore] Re-sync failed — Firestore will reject until backend calls setCustomUserClaims or user re-logs in.');
                }
              } catch (resyncErr) {
                webSdkSyncFailed = true;
                authLogger.warn('Re-sync attempt threw:', resyncErr);
              }
            }
          } else if (auth) {
            authLogger.log('Web SDK has no user, attempting to sync...');
            const storedToken = await secureStorageService.get(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);
            if (storedToken) {
              try {
                // Add timeout to prevent hanging on iOS due to CORS/GAPI issues
                await withTimeout(
                  signInWithCustomToken(auth, storedToken),
                  WEB_SDK_SYNC_TIMEOUT,
                  'Web SDK sync timed out'
                );
                webSdkSynced = true;
                webSdkSyncFailed = false;
                authLogger.log('Successfully synced web SDK auth');
                // Check claims WITHOUT force-refresh — the first token from signInWithCustomToken
                // has the embedded businessId. Force-refreshing would strip it.
                const webUserAfterSync = auth.currentUser;
                if (webUserAfterSync) {
                  const claimsResult = await webUserAfterSync.getIdTokenResult(false).catch(() => null);
                  console.log('[AuthStore] Web SDK claims after authStateChange sync:', {
                    businessId: claimsResult?.claims.businessId ?? null,
                    issuedAtTime: claimsResult?.issuedAtTime,
                  });
                }
              } catch (syncError) {
                // Custom token may have expired (1 hour validity) or timed out
                authLogger.warn('Failed to sync web SDK auth (token may have expired or timed out):', syncError);
                await secureStorageService.remove(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);

                // Try to get a fresh custom token from backend with timeout
                try {
                  const freshSyncSuccess = await withTimeout(
                    attemptWebSdkSyncWithFreshToken(),
                    WEB_SDK_SYNC_TIMEOUT,
                    'Fresh token sync timed out'
                  );
                  if (freshSyncSuccess) {
                    webSdkSynced = true;
                    webSdkSyncFailed = false;
                  } else {
                    webSdkSyncFailed = true;
                  }
                } catch (freshError) {
                  authLogger.warn('Fresh token sync also failed:', freshError);
                  webSdkSyncFailed = true;
                }
              }
            } else {
              authLogger.warn('No stored custom token for web SDK sync');
              // Try to get a fresh custom token from backend with timeout
              try {
                const freshSyncSuccess = await withTimeout(
                  attemptWebSdkSyncWithFreshToken(),
                  WEB_SDK_SYNC_TIMEOUT,
                  'Fresh token sync timed out'
                );
                if (freshSyncSuccess) {
                  webSdkSynced = true;
                  webSdkSyncFailed = false;
                } else {
                  webSdkSyncFailed = true;
                }
              } catch (freshError) {
                authLogger.warn('Fresh token sync failed:', freshError);
                webSdkSyncFailed = true;
              }
            }
          }

          // Set authenticated state - trust native auth even if web SDK sync failed
          // API calls will work (use native token), but Firestore operations may fail
          authLogger.log('authStateChange - Setting state with user:', change.user.uid);
          useAuthStore.setState({
            user: {
              uid: change.user.uid,
              email: change.user.email || null,
              displayName: change.user.displayName || null,
              photoURL: change.user.photoUrl || null,
              emailVerified: change.user.emailVerified || false,
              id: change.user.uid,
              role: 'admin',
            },
            authToken: idTokenResult.token,
            authenticated: true, // Trust native auth
            loading: false,
            authInitialized: true,
          });

          // CRITICAL: Mark authentication as completed
          authenticationCompleted = true;

          authLogger.log('authStateChange - State AFTER set:', {
            authenticated: useAuthStore.getState().authenticated,
            authInitialized: useAuthStore.getState().authInitialized,
            authenticationCompleted: true,
          });

          // Save the auth token to local storage
          localStorage.setItem('authToken', idTokenResult.token);

          // Log web SDK sync status for debugging
          if (!webSdkSynced) {
            authLogger.warn('Web SDK not synced in authStateChange - Firestore operations may fail');
            authLogger.warn('webSdkSyncFailed =', webSdkSyncFailed);
          } else {
            authLogger.log('Web SDK synced successfully in authStateChange');
          }

          // Set up token refresh - only once!
          if (!tokenRefreshIntervalId) {
            tokenRefreshIntervalId = setInterval(() => refreshAuthToken(), 50 * 60 * 1000);
          }
        } else {
          // No user - THIS IS CRITICAL - if this fires after login, it would cause redirect loop
          const currentState = useAuthStore.getState();
          authLogger.log('authStateChange - NO USER RECEIVED');
          authLogger.log('authenticationCompleted:', authenticationCompleted);
          authLogger.log('State BEFORE potential reset:', {
            authenticated: currentState.authenticated,
            authInitialized: currentState.authInitialized,
            hasUser: !!currentState.user,
            userId: currentState.user?.uid,
          });

          // Track in window for debugging
          (window as any).__AUTH_DEBUG__?.push({
            event: 'authStateChange_NO_USER',
            authenticationCompleted,
            stateBefore: {
              authenticated: currentState.authenticated,
              hasUser: !!currentState.user,
            },
            time: Date.now(),
          });

          // CRITICAL: Check multiple conditions before resetting
          // 1. authenticationCompleted flag (set after successful auth)
          // 2. currentState.authenticated (zustand state)
          // 3. currentState.user (actual user object)
          if (authenticationCompleted) {
            authLogger.log('SKIPPING RESET - authenticationCompleted=true');
            authLogger.log('This is likely a race condition - ignoring null user');
          } else if (currentState.authenticated && currentState.user) {
            authLogger.log('SKIPPING RESET - already authenticated with user in state');
            authLogger.log('This might be a race condition - ignoring null user');
          } else {
            authLogger.log('Resetting auth state to unauthenticated');
            useAuthStore.setState({
              user: null,
              authToken: null,
              authenticated: false,
              loading: false,
              authInitialized: true,
            });
          }
        }
      } catch (error) {
        authLogger.error('❌ Error in native authStateChange:', error);

        // Track the error
        (window as any).__AUTH_DEBUG__?.push({
          event: 'authStateChange_ERROR',
          error: error instanceof Error ? error.message : String(error),
          authenticationCompleted,
          time: Date.now(),
        });

        // CRITICAL: Only reset if authentication wasn't already completed
        if (authenticationCompleted) {
          authLogger.warn('Error occurred but authenticationCompleted=true, NOT resetting');
        } else {
          authLogger.error('❌ Resetting auth state due to error');
          useAuthStore.setState({
            user: null,
            authToken: null,
            authenticated: false,
            loading: false,
            authInitialized: true,
          });
        }
      } finally {
        // Resolve the ready promise on first callback
        if (isFirstCallback) {
          isFirstCallback = false;
          if (authReadyResolve && !authReadyResolved) {
            authLogger.log('Auth ready (native) - resolving promise');
            authReadyResolved = true;
            authReadyResolve();
          }
        }
      }
    });

    // Check current user immediately to handle auth state
    // This is critical because authStateChange may not fire if state hasn't changed
    authLogger.log('Calling getCurrentUser()...');
    FirebaseAuthentication.getCurrentUser()
      .then(async (result) => {
        authLogger.log('getCurrentUser RESULT:', result.user?.uid || 'NO USER', 'isFirstCallback:', isFirstCallback);

        // Track in window for debugging
        (window as any).__AUTH_DEBUG__?.push({
          event: 'getCurrentUser',
          user: result.user?.uid || null,
          isFirstCallback,
          time: Date.now(),
        });

        if (result.user && isFirstCallback) {
          // CRITICAL FIX: User exists - initialize auth state immediately
          // Don't wait for authStateChange which may not fire if state hasn't changed
          authLogger.log('getCurrentUser - USER FOUND, initializing auth...');
          authLogger.log('getCurrentUser - User ID:', result.user.uid);

          try {
            // Get ID token
            authLogger.log('getCurrentUser - Getting ID token...');
            const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
            authLogger.log('getCurrentUser - Got ID token:', idTokenResult.token ? 'YES' : 'NO');

            // Attempt web SDK sync for Firestore compatibility
            let webSdkSynced = false;
            const auth = getFirebaseAuth();

            if (auth?.currentUser) {
              // Web SDK session restored from IndexedDB. Check if businessId claim is still present.
              const existingClaims = await auth.currentUser.getIdTokenResult(false).catch(() => null);
              const hasBusinessId = Boolean(existingClaims?.claims.businessId);
              console.log('[AuthStore] getCurrentUser — web SDK session restored:', {
                uid: auth.currentUser.uid,
                hasBusinessId,
                issuedAtTime: existingClaims?.issuedAtTime,
              });
              if (hasBusinessId) {
                webSdkSynced = true;
                webSdkSyncFailed = false;
              } else {
                console.warn('[AuthStore] businessId claim missing from restored web SDK session (getCurrentUser) — attempting re-sync...');
                try {
                  const freshSyncSuccess = await withTimeout(
                    attemptWebSdkSyncWithFreshToken(),
                    WEB_SDK_SYNC_TIMEOUT,
                    'Fresh token re-sync timed out'
                  );
                  if (freshSyncSuccess) {
                    const resynced = auth.currentUser;
                    const resyncedClaims = resynced
                      ? await resynced.getIdTokenResult(false).catch(() => null)
                      : null;
                    console.log('[AuthStore] getCurrentUser re-sync, businessId:', resyncedClaims?.claims.businessId ?? 'STILL MISSING — backend must call setCustomUserClaims');
                    webSdkSynced = Boolean(resyncedClaims?.claims.businessId);
                    webSdkSyncFailed = !webSdkSynced;
                  } else {
                    webSdkSyncFailed = true;
                    console.warn('[AuthStore] getCurrentUser re-sync failed — Firestore will reject until backend fixes claims.');
                  }
                } catch (resyncErr) {
                  webSdkSyncFailed = true;
                  authLogger.warn('getCurrentUser re-sync threw:', resyncErr);
                }
              }
            } else if (auth) {
              authLogger.log('Attempting web SDK sync from getCurrentUser...');
              const storedToken = await secureStorageService.get(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);
              if (storedToken) {
                try {
                  // Add timeout to prevent hanging on iOS due to CORS/GAPI issues
                  await withTimeout(
                    signInWithCustomToken(auth, storedToken),
                    WEB_SDK_SYNC_TIMEOUT,
                    'Web SDK sync timed out'
                  );
                  webSdkSynced = true;
                  webSdkSyncFailed = false;
                  authLogger.log('Successfully synced web SDK auth (getCurrentUser path)');
                  // Check claims WITHOUT force-refresh (preserve embedded custom-token claims)
                  const webUserAfterSync = auth.currentUser;
                  if (webUserAfterSync) {
                    const claimsResult = await webUserAfterSync.getIdTokenResult(false).catch(() => null);
                    console.log('[AuthStore] Web SDK claims after getCurrentUser sync:', {
                      businessId: claimsResult?.claims.businessId ?? null,
                      issuedAtTime: claimsResult?.issuedAtTime,
                    });
                  }
                } catch (syncError) {
                  authLogger.warn('Web SDK sync failed in getCurrentUser (token may have expired or timed out):', syncError);
                  await secureStorageService.remove(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);

                  // Try to get a fresh custom token from backend with timeout
                  try {
                    const freshSyncSuccess = await withTimeout(
                      attemptWebSdkSyncWithFreshToken(),
                      WEB_SDK_SYNC_TIMEOUT,
                      'Fresh token sync timed out'
                    );
                    if (freshSyncSuccess) {
                      webSdkSynced = true;
                      webSdkSyncFailed = false;
                    } else {
                      webSdkSyncFailed = true;
                    }
                  } catch (freshError) {
                    authLogger.warn('Fresh token sync also failed:', freshError);
                    webSdkSyncFailed = true;
                  }
                }
              } else {
                // No stored token - try to get a fresh one with timeout
                try {
                  const freshSyncSuccess = await withTimeout(
                    attemptWebSdkSyncWithFreshToken(),
                    WEB_SDK_SYNC_TIMEOUT,
                    'Fresh token sync timed out'
                  );
                  if (freshSyncSuccess) {
                    webSdkSynced = true;
                    webSdkSyncFailed = false;
                  } else {
                    webSdkSyncFailed = true;
                  }
                } catch (freshError) {
                  authLogger.warn('Fresh token sync failed:', freshError);
                  webSdkSyncFailed = true;
                }
              }
            }

            // Set auth state - trust native auth even if web SDK sync failed
            authLogger.log('getCurrentUser - Setting auth state NOW...');
            useAuthStore.setState({
              user: {
                uid: result.user.uid,
                email: result.user.email || null,
                displayName: result.user.displayName || null,
                photoURL: result.user.photoUrl || null,
                emailVerified: result.user.emailVerified || false,
                id: result.user.uid,
                role: 'admin',
              },
              authToken: idTokenResult.token,
              authenticated: true, // Trust native auth
              loading: false,
              authInitialized: true,
            });

            // CRITICAL: Mark authentication as completed to prevent race condition resets
            authenticationCompleted = true;

            // VERIFY the state was actually set
            const verifyState = useAuthStore.getState();
            authLogger.log('getCurrentUser - State AFTER setState:', {
              authenticated: verifyState.authenticated,
              authInitialized: verifyState.authInitialized,
              hasUser: !!verifyState.user,
              userId: verifyState.user?.uid,
              authenticationCompleted: true,
            });

            // Track in debug array
            (window as any).__AUTH_DEBUG__?.push({
              event: 'getCurrentUser_STATE_SET',
              authenticated: verifyState.authenticated,
              authInitialized: verifyState.authInitialized,
              authenticationCompleted: true,
              time: Date.now(),
            });

            localStorage.setItem('authToken', idTokenResult.token);

            if (!webSdkSynced) {
              authLogger.log('getCurrentUser - Web SDK NOT synced, Firestore may fail');
            } else {
              authLogger.log('getCurrentUser - Web SDK synced OK');
            }

            // Set up token refresh if not already set
            if (!tokenRefreshIntervalId) {
              tokenRefreshIntervalId = setInterval(() => refreshAuthToken(), 50 * 60 * 1000);
            }
          } catch (error) {
            authLogger.log('getCurrentUser - ERROR in try block:', error);
            authLogger.error('authenticationCompleted:', authenticationCompleted);

            // Track the error
            (window as any).__AUTH_DEBUG__?.push({
              event: 'getCurrentUser_ERROR',
              error: error instanceof Error ? error.message : String(error),
              authenticationCompleted,
              time: Date.now(),
            });

            // CRITICAL: Only reset if authentication wasn't already completed elsewhere
            if (authenticationCompleted) {
              authLogger.warn('getCurrentUser - Error but auth already completed, NOT resetting');
            } else {
              authLogger.log('getCurrentUser - Resetting auth state due to error');
              useAuthStore.setState({
                user: null,
                authToken: null,
                authenticated: false,
                loading: false,
                authInitialized: true,
              });
            }
          }

          // Resolve auth ready
          isFirstCallback = false;
          if (authReadyResolve && !authReadyResolved) {
            authLogger.log('getCurrentUser - RESOLVING AUTH READY (with user)');
            authReadyResolved = true;
            authReadyResolve();
            authLogger.log('getCurrentUser - AUTH READY RESOLVED!');

            // Track this critical moment
            (window as any).__AUTH_DEBUG__?.push({
              event: 'AUTH_READY_RESOLVED_WITH_USER',
              time: Date.now(),
            });
          }
        } else if (!result.user && isFirstCallback) {
          // No user - resolve auth ready
          authLogger.log('getCurrentUser - NO USER found, showing login');
          isFirstCallback = false;
          if (authReadyResolve && !authReadyResolved) {
            authLogger.log('getCurrentUser - RESOLVING AUTH READY (no user)');
            authReadyResolved = true;
            useAuthStore.setState({ authInitialized: true, loading: false });
            authReadyResolve();
            authLogger.log('getCurrentUser - AUTH READY RESOLVED (no user)!');

            // Track this
            (window as any).__AUTH_DEBUG__?.push({
              event: 'AUTH_READY_RESOLVED_NO_USER',
              time: Date.now(),
            });
          }
        }
      })
      .catch((error) => {
        authLogger.error('getCurrentUser error:', error);
        if (isFirstCallback) {
          isFirstCallback = false;
          if (authReadyResolve && !authReadyResolved) {
            authLogger.log('Auth ready - resolved due to getCurrentUser error');
            authReadyResolved = true;
            useAuthStore.setState({ authInitialized: true, loading: false });
            authReadyResolve();
          }
        }
      });
  } else {
    // Use web SDK listener
    authLogger.log('Setting up onAuthStateChanged listener...');
    const auth = getFirebaseAuth();
    if (!auth) {
      authLogger.warn('Firebase auth not initialized for web');
      useAuthStore.setState({ authInitialized: true, loading: false });
      if (authReadyResolve && !authReadyResolved) {
        authReadyResolved = true;
        authReadyResolve();
      }
      return;
    }
    authLogger.log('Got Firebase Auth instance, current user:', auth.currentUser?.uid || 'none');

    onAuthStateChanged(auth, async (user) => {
      authLogger.log('onAuthStateChanged callback fired, user:', user?.uid || 'null');
      try {
        if (user) {
          // Auth V2 device sessions (uid = `businessDevice:{id}`) authenticate via a
          // custom token and have no verified email; treat them as authenticated
          // alongside regular email-verified users. Regular logins are unaffected.
          const isBusinessDeviceUser =
            typeof user.uid === 'string' && user.uid.startsWith('businessDevice:');
          if (user.emailVerified || isBusinessDeviceUser) {
            const profile = {
              displayName: user.displayName,
              email: user.email,
              photoURL: user.photoURL,
            };

            const authToken = await user.getIdToken(true);

            useAuthStore.setState({
              user: {
                ...user,
                ...profile,
                id: user.uid,
                role: 'admin',
              },
              authToken,
              authenticated: true,
              loading: false,
              authInitialized: true,
            });

            // Save the auth token to local storage
            localStorage.setItem('authToken', authToken);

            // Set up token refresh - only once!
            if (!tokenRefreshIntervalId) {
              tokenRefreshIntervalId = setInterval(() => refreshAuthToken(), 50 * 60 * 1000);
            }
          } else {
            // User exists but email not verified
            useAuthStore.setState({
              user: null,
              authToken: null,
              authenticated: false,
              loading: false,
              authInitialized: true,
            });
          }
        } else {
          // No user
          useAuthStore.setState({
            user: null,
            authToken: null,
            authenticated: false,
            loading: false,
            authInitialized: true,
          });
        }
      } catch (error) {
        authLogger.error('Error in onAuthStateChanged:', error);
        useAuthStore.setState({
          user: null,
          authToken: null,
          authenticated: false,
          loading: false,
          authInitialized: true,
        });
      } finally {
        // Resolve the ready promise on first callback
        if (isFirstCallback) {
          isFirstCallback = false;
          if (authReadyResolve && !authReadyResolved) {
            authLogger.log('Auth ready - resolving promise');
            authReadyResolved = true;
            authReadyResolve();
          }
        }
      }
    });
  }
}

// For web platform, initialize immediately since there's no native bridge delay
// For native platforms, this will be called from AppUpdaterGuard after initialization
if (!Capacitor.isNativePlatform()) {
  initializeAuthListener();
}

// Helper function to wait for auth initialization
// const waitForAuthInitialization = () =>
//   new Promise((resolve) => {
//     const unsubscribe = onAuthStateChanged(AUTH, (user) => {
//       useAuthStore.setState({ authInitialized: true });
//       unsubscribe();
//       resolve(user);
//     });
//   });

export default useAuthStore;

export async function exchangeRefreshTokenForIdToken(refreshToken: string): Promise<string | null> {
  try {
    const response = await axios.post('https://oauth2.googleapis.com/token', null, {
      params: {
        client_id: 'your-client-id',
        client_secret: 'your-client-secret',
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      },
    });

    const { id_token } = response.data;
    return id_token;
  } catch (error) {
    authLogger.error('Error exchanging refresh token for ID token:', error);
    return null;
  }
}
